import { userTypes } from '../types'
import { INote } from '../types/NoteTypes';

type UserToClient = (userData: userTypes.BaseUserInfo, uid: string, company?: string, member?: string)
    => userTypes.Client;

type UserToConsultant = (userData: userTypes.ConsultantUserInfo, uid: string, company?: string, member?: string)
    => userTypes.Consultant;

type UserToReferralPartner = (uid: string, userData: userTypes.ReferralPartnerUserInfo, metadata: Record<any, any>) => userTypes.ReferralPartner

type UserToSubscriber = (uid: string, userData: Record<any, any>, stripeId: string, cus_id: string) => userTypes.Subscriber

export const userDataToClient = (userData: Record<any, any>, uid:string, company:string, member:string) => ({
    userId: uid,
    userType: userTypes.AccountTypes.CLIENT,
    userInfo: {
        name: userData.name,
        dob: userData.dob,
        gender: userData.gender,
        email: userData.email,
        address: userData.address,
        city: userData.city,
        state: userData.state,
        zip: userData.zip,
        phone: userData.phone,
        companyId: company || "N/A",
        memberId: member || "N/A",
    },
    metadata: {
        firstLogin: true,
        createdAt: new Date(),
        updatedAt: new Date(),
        agreedToTerms: true,
        cus_id: '',
        stripeId: '',
    },
    neatt: {
        fk: {
            attendedCoursesOrWorkshops: userData.attendedCoursesOrWorkshops,
            experienceLevel: userData.experienceLevel,
            familiarWithD2I: userData.familiarWithD2I,
            financialPlanInPlace: userData.financialPlanInPlace,
            savingForRetirement: {
                active: userData.savingForRetirement,
                amountPerMonth: userData.amountPerMonth
            },
            usingToolsOrApps: userData.usingToolsOrApps,
            workedWithAdvisor: userData.workedWithAdvisor 
        },
        ei: {
            additionalIncome: userData.additionalIncome,
            addressDuration: userData.addressDuration,
            changesToIncomeOverYear: userData.changesToIncomeOverYear,
            documentationConsent: userData.documentationConsent,
            employment: {
                duration: userData.employmentDuration,
                status: userData.employmentStatus,
            },
            livingStatus: userData.livingStatus
        },
        lt: {
            concerns: {
                active: userData.activeConcerns,
                reason: userData.reasonforconcerns,
            },
            governmentBackedLoans: userData.governmentBackedLoans,
            loan: {
                seeking: userData.seekingLoan,
                amount: userData.loanamount,
                type: userData.loanType,
            },
            openToExploring: userData.openToExploring
        },
        fh: {
            appliedForLoan: {
                applied: userData.applied,
                denied: userData.denied,
            },
            awareOfCreditScore: userData.awareOfCreditScore,
            behindOnPayments: userData.behindOnPayments,
            checkedCreditInLastYear: userData.checkedCreditInLastYear,
            collectionsOrJudgements: userData.collectionsOrJudgements,
            cosignerOrGuaranteeForClient: userData.cosignerOrGuaranteeForClient,
            cosignerOrGuaranteeForOther: userData.cosignerOrGuaranteeForOther,
            placedBlockOnCredit: userData.placedBlockOnCredit,
            studentLoanDebt: userData.studentLoanDebt,
            using30Percent: userData.using30Percent,
            unableToPay: {
                foreclosureOrDefault: userData.foreclosureOrDefault,
                reason: userData.reason,
            }
        },
        fg: {
            dependents: userData.dependents,
            experiencedHarships: userData.experiencedHarships,
            howDidTheyHear: userData.howDidTheyHear,
            moneySetAside: userData.asideMoney,
            percentIncomeTowardsHousing: userData.percentageIncome,
            primaryGoal: userData.primaryGoal,
            stepsToImprove: userData.stepsToImprove,
            supportSystem: userData.supportSystem,
            timeline: userData.timeline,
            upcomingLargeExpenses: userData.largeExpense,
            lifeChanges: {
                active: userData.lifeChanges,
                type: userData.lifeType
            },
            alimonyOrChildSupport: {
                active: userData.paying,
                type: userData.payType
            }
        }
    },
    notes: [
        {
            id: 0,
            text: `Account Created ${new Date().toLocaleString()}`,
            date: new Date(),
            author: {
                id: '9438b498-00f1-70bd-860f-26da3b9cc48a',
                name: 'ClientVitals'
            }
        }
    ],
});

export const userDataToConsultant: UserToConsultant = (userData, uid) => ({
    userId: uid,
    userType: userTypes.AccountTypes.NEATT,
    userInfo: {
        name: userData.name,
        email: userData.email,
        phone: userData.phone,
        source: userData.source
    },
    metadata: {
        firstLogin: true,
        createdAt: new Date(),
        updatedAt: new Date(),
        agreedToTerms: true,
        cus_id: '',
        stripeId: ''
    }
});

export const userDataToReferralPartner: UserToReferralPartner = (uid, userData, metadata) => ({
    userId: uid,
    userType: userTypes.AccountTypes.REFERRAL,
    userInfo: {
        representativeName: userData.representativeName,
        companyName: userData.companyName,
        websiteURL: userData.websiteURL,
        email: userData.email,
        address: userData.address,
        city: userData.city,
        state: userData.state,
        zip: userData.zip,
        phone: userData.phone,
    },
    metadata: {
        firstLogin: true,
        createdAt: new Date(),
        updatedAt: new Date(),
        agreedToTerms: true,
        reasonForPartnership: metadata.reasonForPartnership,
        expectations: metadata.expectations,
        sourceOfClients: metadata.sourceOfClients,
        references: metadata.references,
        clientsPerMonth: metadata.clientsPerMonth,
        howDidYouHear: metadata.howDidYouHear,
        referralCount: 0,
        stripeId: 'N/A',
        cus_id: 'N/A'
    }
});

export const userDataToSubscriber: UserToSubscriber = (uid, userData, stripeId: string, cus_id: string) => ({
    userId: uid,
    userType: userTypes.AccountTypes.COMPANY,
    userInfo: {
        name: userData.name,
        email: userData.email,
        address: userData.address,
        city: userData.city,
        state: userData.state,
        zip: userData.zip,
        phone: userData.phone,
        reasonForSubscription: userData.reasonForSubscription,
        referredBy: userData?.referredBy
    },
    metadata: {
        firstLogin: true,
        createdAt: new Date(),
        updatedAt: new Date(),
        agreedToTerms: true,
        active: false,
        stripeId,
        cus_id
    }
});

export const addClientNote = (user: userTypes.Client, note: INote) => {
    if (user.notes) {
        user.notes.push(note);
    } else {
        user.notes = [note];
    }
}